<script>
import HomeAdminDashboardComponent from '../../features/dashboards/HomeAdminDashboardComponent';

export default {
  name: 'HomeAdminDashboardView',
  components: {},
  extends: HomeAdminDashboardComponent,
};
</script>

<template>
  <div class="home-admin-dashboard-view">
    <!-- Banner de Resumen / Estado Ejecutivo -->
    <div class="admin_welcome_card">
      <div class="welcome_info">
        <div class="welcome_badge_icon">
          <i class="fas fa-chart-line" />
        </div>
        <div>
          <h4 class="welcome_title">
            Panel de Control y Resumen General
          </h4>
          <p class="welcome_subtitle">
            Supervisa en tiempo real los indicadores de usuarios, suscripciones activas y actividad en cursos.
          </p>
        </div>
      </div>
      <div class="welcome_actions">
        <span class="status_live_chip">
          <span class="live_dot" /> Plataforma Activa
        </span>
        <el-button
          size="small"
          class="btn_refresh_dashboard"
          :loading="isRefreshing"
          @click="onRefresh"
        >
          <i
            class="fas fa-sync-alt mr-1"
            :class="{ 'fa-spin': isRefreshing }"
          /> Actualizar
        </el-button>
      </div>
    </div>

    <!-- Sección 1: Usuarios e Inscripciones -->
    <div class="dash_section_header">
      <div class="dash_section_title">
        <i class="fas fa-users-cog" /> Usuarios e Inscripciones
        <span
          v-if="currentUserStats"
          class="dash_section_count"
        >
          {{ totalUsers }} usuarios
        </span>
      </div>
      <router-link
        to="/admin/autorizacion/usuarios"
        class="dash_section_link"
      >
        Gestionar Usuarios <i class="fas fa-arrow-right" />
      </router-link>
    </div>

    <div
      v-if="currentUserStats"
      class="account_stats_grid"
    >
      <div class="account_stat_card">
        <div class="account_stat_icon is_green">
          <i class="fas fa-user-check" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentUserStats.totalUsersActive }}</span>
          <span class="account_stat_label">Usuarios Activos</span>
          <span
            v-if="totalUsers > 0"
            class="account_stat_meta text-success"
          >
            <i class="fas fa-check-circle" /> {{ activeUsersPercentage }}% del total
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_red">
          <i class="fas fa-user-slash" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentUserStats.totalUsersInactive }}</span>
          <span class="account_stat_label">Usuarios Inactivos</span>
          <span
            v-if="totalUsers > 0"
            class="account_stat_meta text-muted"
          >
            {{ 100 - activeUsersPercentage }}% suspendidos
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_blue">
          <i class="fas fa-id-card" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentUserStats.totalSubscriptions }}</span>
          <span class="account_stat_label">Total Inscripciones</span>
          <span class="account_stat_meta text-primary">
            Histórico registrado
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_navy">
          <i class="fas fa-crown" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentUserStats.totalSubscriptionsActive }}</span>
          <span class="account_stat_label">Inscripciones Activas</span>
          <span
            v-if="currentUserStats.totalSubscriptions > 0"
            class="account_stat_meta text-success"
          >
            <i class="fas fa-arrow-up" /> {{ activeSubsPercentage }}% vigentes
          </span>
        </div>
      </div>
    </div>

    <!-- Sección 2: Rendimiento e Interacción en Cursos -->
    <div class="dash_section_header">
      <div class="dash_section_title">
        <i class="fas fa-graduation-cap" /> Rendimiento e Interacción en Cursos
        <span
          v-if="currentCourseStats"
          class="dash_section_count"
        >
          4 métricas
        </span>
      </div>
      <router-link
        to="/admin/cursos"
        class="dash_section_link"
      >
        Ver Catálogo de Cursos <i class="fas fa-arrow-right" />
      </router-link>
    </div>

    <div
      v-if="currentCourseStats"
      class="account_stats_grid"
    >
      <div class="account_stat_card">
        <div class="account_stat_icon is_amber">
          <i class="fas fa-star" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentCourseStats.totalFavorite }}</span>
          <span class="account_stat_label">Cursos en Favoritos</span>
          <span class="account_stat_meta text-muted">
            Guardados por alumnos
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_green">
          <i class="fas fa-thumbs-up" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentCourseStats.totalLiked }}</span>
          <span class="account_stat_label">Reacciones "Me Gusta"</span>
          <span
            v-if="totalReactions > 0"
            class="account_stat_meta text-success"
          >
            <i class="fas fa-heart" /> {{ likedPercentage }}% de aprobación
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_red">
          <i class="fas fa-thumbs-down" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentCourseStats.totalDisliked }}</span>
          <span class="account_stat_label">Reacciones "No Me Gusta"</span>
          <span
            v-if="totalReactions > 0"
            class="account_stat_meta text-muted"
          >
            {{ 100 - likedPercentage }}% desaprobación
          </span>
        </div>
      </div>

      <div class="account_stat_card">
        <div class="account_stat_icon is_purple">
          <i class="fas fa-trophy" />
        </div>
        <div class="account_stat_body">
          <span class="account_stat_value">{{ currentCourseStats.totalFinished }}</span>
          <span class="account_stat_label">Cursos Finalizados</span>
          <span class="account_stat_meta text-primary">
            Completados con éxito
          </span>
        </div>
      </div>
    </div>

    <!-- Sección 3: Accesos Rápidos de Gestión -->
    <div class="dash_section_header">
      <div class="dash_section_title">
        <i class="fas fa-th-large" /> Accesos Rápidos de Gestión
        <span class="dash_section_count">6 accesos directos</span>
      </div>
    </div>

    <div class="admin_shortcuts_grid">
      <router-link
        to="/admin/autorizacion/usuarios"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_blue">
            <i class="fas fa-users" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Usuarios del Sistema
            </h5>
            <p class="shortcut_desc">
              Administra cuentas, roles y estados de acceso.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>

      <router-link
        to="/admin/cursos"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_purple">
            <i class="fas fa-book-open" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Catálogo de Cursos
            </h5>
            <p class="shortcut_desc">
              Gestiona temarios, videos y recursos didácticos.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>

      <router-link
        to="/admin/simuladores"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_navy">
            <i class="fas fa-laptop-code" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Simuladores de Examen
            </h5>
            <p class="shortcut_desc">
              Configura pruebas, áreas y bancos de reactivos.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>

      <router-link
        to="/admin/inscripciones"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_green">
            <i class="fas fa-id-card" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Inscripciones y Planes
            </h5>
            <p class="shortcut_desc">
              Supervisa membresías vigentes y asignaciones.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>

      <router-link
        to="/admin/descuentos"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_amber">
            <i class="fas fa-tags" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Cupones y Descuentos
            </h5>
            <p class="shortcut_desc">
              Crea códigos promocionales, cupos y vigencias.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>

      <router-link
        to="/admin/autorizacion/roles"
        class="admin_shortcut_card"
      >
        <div class="shortcut_content">
          <div class="shortcut_icon is_red">
            <i class="fas fa-user-shield" />
          </div>
          <div class="shortcut_text">
            <h5 class="shortcut_title">
              Roles y Permisos
            </h5>
            <p class="shortcut_desc">
              Configura la matriz de privilegios por módulo.
            </p>
          </div>
        </div>
        <i class="fas fa-chevron-right shortcut_arrow" />
      </router-link>
    </div>
  </div>
</template>
